import { DropdownController } from './dropdown-controller.js';
import { DASHBOARD_CONFIG } from '../config.js';

const STRINGS = {
    noDescription: 'Không có mô tả',
    publicLabel: 'Công khai',
    privateLabel: 'Riêng tư',
    setPrivate: 'Đặt riêng tư',
    setPublic: 'Đặt công khai',
    projectSuffix: 'dự án',
    viewSuffix: 'lượt xem',
    nearLimitWarning: 'Collection đang gần đạt giới hạn tối đa',
};

export function initCollectionRenderer() {
    this.collectionListContainer = document.getElementById('collections-list-container');
    this.collectionItemTemplate = document.getElementById('collection-item-template');
    this.collectionTagTemplate = document.getElementById('collection-tag-template');
    const fallbackMax = DASHBOARD_CONFIG.MAX_ITEMS_PER_COLLECTION;
    this.collectionMaxItems = parseInt(this.collectionListContainer?.dataset?.maxItems || fallbackMax, 10);
    this._collectionActionControllers = new Set();

    if (this.collectionListContainer) {
        this.collectionStrings = {
            ...STRINGS,
            noDescription: this.collectionListContainer.dataset.noDescription || STRINGS.noDescription,
            publicLabel: this.collectionListContainer.dataset.publicLabel || STRINGS.publicLabel,
            privateLabel: this.collectionListContainer.dataset.privateLabel || STRINGS.privateLabel,
            setPrivate: this.collectionListContainer.dataset.setPrivate || STRINGS.setPrivate,
            setPublic: this.collectionListContainer.dataset.setPublic || STRINGS.setPublic,
            projectSuffix: this.collectionListContainer.dataset.projectSuffix || STRINGS.projectSuffix,
            viewSuffix: this.collectionListContainer.dataset.viewSuffix || STRINGS.viewSuffix,
            nearLimitWarning: this.collectionListContainer.dataset.warningText || STRINGS.nearLimitWarning,
        };
    } else {
        this.collectionStrings = STRINGS;
    }

    if (!this._collectionDropdownCloser) {
        this._collectionDropdownCloser = (event) => {
            if (event.target.closest('.actions-dropdown-btn') || event.target.closest('.actions-dropdown-menu')) {
                return;
            }
            this.closeAllActionDropdowns();
        };
        document.addEventListener('click', this._collectionDropdownCloser);
    }
}

export function renderCollectionsList(collections) {
    if (!this.collectionListContainer) {
        return;
    }

    this.destroyCollectionDropdowns();
    this.collectionListContainer.innerHTML = '';
    this.closeAllActionDropdowns();

    if (!collections || collections.length === 0) {
        if (typeof this.refreshBulkSelectionBindings === 'function') {
            this.refreshBulkSelectionBindings();
        }
        return;
    }

    collections.forEach((collection) => {
        const item = this.createCollectionItem(collection);
        if (item) {
            this.collectionListContainer.appendChild(item);
        }
    });

    if (typeof this.refreshBulkSelectionBindings === 'function') {
        this.refreshBulkSelectionBindings();
    }
}

export function createCollectionItem(collection) {
    if (!this.collectionItemTemplate) {
        return null;
    }

    const fragment = this.collectionItemTemplate.content.cloneNode(true);
    const item = fragment.querySelector('.collection-item');
    item.setAttribute('data-collection-id', collection.id);
    item.dataset.collectionId = collection.id;
    item.dataset.collectionPublic = collection.is_public ? 'true' : 'false';

    const checkbox = item.querySelector('.collection-checkbox');
    if (checkbox) {
        checkbox.setAttribute('data-collection-id', collection.id);
    }

    const nameElement = item.querySelector('.collection-name');
    if (nameElement) {
        nameElement.textContent = collection.name;
        nameElement.title = collection.name;
    }

    const description = item.querySelector('.collection-description');
    if (description) {
        description.textContent = collection.description || this.collectionStrings.noDescription;
    }

    this.applyVisibilityBadge(item, collection);
    this.populateCollectionStats(item, collection);
    this.populateCollectionTags(item, collection);
    this.applyWarningState(item, collection);
    this.applyCollectionThumbnail(item, collection);
    this.registerCollectionActions(item, collection);

    return item;
}

export function applyCollectionThumbnail(item, collection) {
    const thumbnail = item.querySelector('.collection-thumbnail');
    if (!thumbnail) {
        return;
    }

    /* Inject Lucide book-marked SVG icon */
    thumbnail.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" 
             stroke="currentColor" stroke-width="2" stroke-linecap="round" 
             stroke-linejoin="round" aria-hidden="true">
            <path d="M10 2v8l3-3 3 3V2"/>
            <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"/>
        </svg>
    `;

    /* Apply color theme based on collection status */
    const theme = getCollectionTheme(collection, this.collectionMaxItems);
    thumbnail.setAttribute('data-collection-theme', theme);
}

function getCollectionTheme(collection, maxItems) {
    /* Warning state: approaching limit */
    if (maxItems && collection.item_count >= Math.floor(maxItems * 0.9)) {
        return 'amber';
    }

    /* Public collections: green theme */
    if (collection.is_public) {
        return 'green';
    }

    /* Private collections: gray theme */
    return 'gray';
}

export function applyVisibilityBadge(item, collection) {
    const badge = item.querySelector('.collection-visibility');
    const icon = item.querySelector('.visibility-icon');
    const text = item.querySelector('.visibility-text');

    if (!badge || !icon || !text) {
        return;
    }

    if (collection.is_public) {
        badge.className = 'collection-visibility inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800';
        icon.className = 'visibility-icon w-1.5 h-1.5 rounded-full mr-1 bg-green-500';
        text.textContent = this.collectionStrings.publicLabel;
    } else {
        badge.className = 'collection-visibility inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800';
        icon.className = 'visibility-icon w-1.5 h-1.5 rounded-full mr-1 bg-gray-500';
        text.textContent = this.collectionStrings.privateLabel;
    }

    const toggleText = item.querySelector('.visibility-toggle-text');
    if (toggleText) {
        toggleText.textContent = collection.is_public ? this.collectionStrings.setPrivate : this.collectionStrings.setPublic;
    }
}

export function populateCollectionStats(item, collection) {
    const itemsCount = item.querySelector('.items-count');
    if (itemsCount) {
        itemsCount.textContent = `${collection.item_count || 0} ${this.collectionStrings.projectSuffix}`;
    }

    const viewCountWrapper = item.querySelector('.view-count-wrapper');
    const viewCount = item.querySelector('.view-count');

    if (collection.is_public) {
        if (viewCount) {
            viewCount.textContent = `${collection.view_count || 0} ${this.collectionStrings.viewSuffix}`;
        }
        viewCountWrapper?.classList.remove('hidden');
    } else {
        viewCountWrapper?.classList.add('hidden');
    }

    const createdDate = item.querySelector('.created-date');
    if (createdDate) {
        createdDate.textContent = formatRelativeDate(collection.created_at);
    }
}

export function populateCollectionTags(item, collection) {
    const container = item.querySelector('.collection-tags');
    if (!container || !this.collectionTagTemplate) {
        return;
    }

    container.innerHTML = '';
    if (!Array.isArray(collection.tags)) {
        return;
    }

    collection.tags.forEach((tag) => {
        const fragment = this.collectionTagTemplate.content.cloneNode(true);
        const tagElement = fragment.querySelector('.collection-tag');
        if (tagElement) {
            tagElement.textContent = tag;
            container.appendChild(tagElement);
        }
    });
}

export function applyWarningState(item, collection) {
    if (!this.collectionMaxItems) {
        return;
    }

    const warning = item.querySelector('.collection-warning');
    if (!warning) {
        return;
    }

    const threshold = DASHBOARD_CONFIG.SIZE_WARNING_THRESHOLD;
    if (collection.item_count >= Math.floor(this.collectionMaxItems * threshold)) {
        warning.classList.remove('hidden');
        const text = warning.querySelector('.warning-text');
        if (text) {
            text.textContent = this.collectionStrings.nearLimitWarning;
        }
    } else {
        warning.classList.add('hidden');
    }
}

export function registerCollectionActions(item, collection) {
    const dropdownBtn = item.querySelector('.actions-dropdown-btn');
    const dropdownMenu = item.querySelector('.actions-dropdown-menu');

    if (dropdownBtn && dropdownMenu) {
        const controller = new DropdownController(dropdownBtn, dropdownMenu, {
            closeOthers: true,
        });
        this._collectionActionControllers.add(controller);
    }

    const setAction = (selector, action, extras = {}) => {
        const element = item.querySelector(selector);
        if (!element) {
            return;
        }
        element.dataset.collectionAction = action;
        element.dataset.collectionId = collection.id;
        Object.entries(extras).forEach(([key, value]) => {
            element.dataset[key] = value;
        });
    };

    setAction('.view-items', 'view-items');
    setAction('.edit-collection', 'edit');
    setAction('.duplicate-collection', 'duplicate');
    setAction('.toggle-visibility', 'toggle-visibility', {
        nextVisibility: collection.is_public ? 'private' : 'public',
    });
    setAction('.delete-collection', 'delete');
    setAction('.share-collection', 'share');
    setAction('.view-public-link', 'view-public');
    setAction('.collection-name', 'view-items');
    setAction('.view-items-link', 'view-items');
}

export function closeAllActionDropdowns(exclude = null) {
    this._collectionActionControllers?.forEach((controller) => {
        if (exclude && controller.menu === exclude) {
            return;
        }
        controller.close();
    });
}

function formatRelativeDate(dateString) {
    if (!dateString) {
        return '';
    }

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.max(Math.floor(diffMs / (1000 * 60 * 60 * 24)), 0);

    if (diffDays <= 1) {
        return '1 ngày trước';
    }

    if (diffDays < 7) {
        return `${diffDays} ngày trước`;
    }

    if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} tuần trước`;
    }

    const months = Math.floor(diffDays / 30);
    return `${months} tháng trước`;
}

export function teardownCollectionRenderer() {
    if (this._collectionDropdownCloser) {
        document.removeEventListener('click', this._collectionDropdownCloser);
        this._collectionDropdownCloser = null;
    }
    this.destroyCollectionDropdowns();
}

export function destroyCollectionDropdowns() {
    if (!this._collectionActionControllers) {
        return;
    }

    this._collectionActionControllers.forEach((controller) => controller.destroy());
    this._collectionActionControllers.clear();
}
